import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  GraduationCap, BookOpen, Briefcase, Settings, School, UserCheck, Users,
  CheckCircle2, Clock, ArrowRight, Plus, RefreshCw, FileQuestion,
  TrendingUp, BarChart2, Award, Sparkles, Layers,
  ExternalLink, HelpCircle, Check, AlertCircle
} from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import AppLayout from '../layout/AppLayout';
import { dashboardAPI } from '../../services/api';
import { COLORS } from '../../utils/constants';

const C = COLORS;

const DIFFICULTY_COLORS = {
  'Easy': '#10B981',
  'Medium': '#F59E0B',
  'Hard': '#EF4444',
  'Any': '#6366F1',
  'Unspecified': '#9CA3AF',
};

const PAL_TYPE_CONFIG = {
  'Practice': { color: '#3B82F6', bg: '#EFF6FF', border: '#BFDBFE', path: '/pal/practice', addPath: '/pal/practice/add', desc: 'Self-paced questions for student mastery and skill drills' },
  'Tests': { color: '#EF4444', bg: '#FEF2F2', border: '#FECACA', path: '/pal/tests', addPath: '/pal/tests/add', desc: 'Timed assessments, formal mock exams & competitive tests' },
  'Learning': { color: '#10B981', bg: '#ECFDF5', border: '#A7F3D0', path: '/pal/learning', addPath: '/pal/learning/add', desc: 'Structured learning modules with step-by-step guidance' },
  'Homework': { color: '#F59E0B', bg: '#FFFBEB', border: '#FDE68A', path: '/pal/homework', addPath: '/pal/homework/add', desc: 'Assigned homework sets, practice sheets & class tasks' },
};

const MetricCard = ({ title, value, subtitle, icon: Icon, color, bg, border, linkText, to, trend }) => {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => to && navigate(to)}
      style={{
        background: '#fff',
        borderRadius: 16,
        border: `1px solid ${border || C.g200}`,
        padding: '20px 22px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.02)',
        cursor: to ? 'pointer' : 'default',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
      onMouseEnter={(e) => {
        if (to) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
          e.currentTarget.style.borderColor = color;
        }
      }}
      onMouseLeave={(e) => {
        if (to) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.02)';
          e.currentTarget.style.borderColor = border || C.g200;
        }
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.g500, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
            {title}
          </span>
          <div style={{ fontSize: 32, fontWeight: 800, color: C.g900, marginTop: 4, letterSpacing: '-0.02em' }}>
            {typeof value === 'number' ? value.toLocaleString() : value || 0}
          </div>
        </div>
        <div style={{
          width: 48,
          height: 48,
          borderRadius: 14,
          background: bg || C.primaryGhost,
          color: color || C.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon size={24} />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 10, borderTop: `1px solid ${C.g100}` }}>
        <span style={{ fontSize: 12, color: C.g600, fontWeight: 500 }}>
          {subtitle}
        </span>
        {to && (
          <span style={{ fontSize: 12, fontWeight: 600, color: color || C.primary, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {linkText || 'View'} <ArrowRight size={13} />
          </span>
        )}
      </div>
    </div>
  );
};

const AdvansityDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const navigate = useNavigate();

  const fetchStats = async (isManual = false) => {
    try {
      if (isManual) setRefreshing(true);
      else setLoading(true);
      setError(null);
      const res = await dashboardAPI.advansity();
      setData(res.data);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('Error fetching Advansity dashboard data:', err);
      setError('Failed to load Advansity stats. Please verify database connection.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <AppLayout title="Advansity Dashboard">
        <div style={{ padding: '60px 20px', textAlign: 'center' }}>
          <div style={{
            width: 48,
            height: 48,
            border: '3px solid #E5E7EB',
            borderTopColor: C.primary,
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 16px',
          }} />
          <h3 style={{ margin: 0, fontSize: 18, color: C.g800, fontWeight: 600 }}>Loading Advansity Intelligence...</h3>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: C.g500 }}>Aggregating counts from PAL, Management, and Curriculum databases</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </AppLayout>
    );
  }

  if (error || !data) {
    return (
      <AppLayout title="Advansity Dashboard">
        <div style={{ padding: 40, textAlign: 'center', background: '#fff', borderRadius: 16, border: `1px solid ${C.g200}` }}>
          <AlertCircle size={44} color={C.danger} style={{ margin: '0 auto 16px' }} />
          <h3 style={{ margin: 0, fontSize: 18, color: C.g900, fontWeight: 700 }}>Unable to load dashboard data</h3>
          <p style={{ margin: '8px 0 20px', fontSize: 14, color: C.g600 }}>{error || 'An unexpected error occurred.'}</p>
          <button
            onClick={() => fetchStats(true)}
            style={{
              padding: '10px 20px',
              background: C.primary,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <RefreshCw size={16} /> Try Again
          </button>
        </div>
      </AppLayout>
    );
  }

  const { pal, management, advansity, recentQuizzes, recentSubmissions } = data;

  // Chart data formatting
  const subjectChartData = (advansity?.questions?.topSubjects || []).map(s => ({
    name: s.name.length > 15 ? s.name.substring(0, 14) + '…' : s.name,
    fullName: s.name,
    count: s.count,
  }));

  const difficultyChartData = (advansity?.questions?.byLevel || []).map(l => ({
    name: l.name,
    value: l.count,
    color: DIFFICULTY_COLORS[l.name] || '#6366F1',
  }));

  const palPieData = (pal?.byType || []).filter(p => p.value > 0);

  return (
    <AppLayout title="Advansity Dashboard">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 28, paddingBottom: 40 }}>

        {/* ─── Hero Header & Status Bar ─── */}
        <div style={{
          background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 50%, #4338CA 100%)',
          borderRadius: 20,
          padding: '28px 32px',
          color: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 20,
          boxShadow: '0 10px 25px -5px rgba(49, 46, 129, 0.3)',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Subtle decorative background circles */}
          <div style={{ position: 'absolute', top: -40, right: -40, width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -60, left: '30%', width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1, maxWidth: 640 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{
                background: 'rgba(255,255,255,0.18)',
                padding: '4px 10px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6
              }}>
                <Sparkles size={13} color="#FDE047" /> Institutional Overview
              </span>
              <span style={{
                background: 'rgba(16, 185, 129, 0.25)',
                color: '#6EE7B7',
                border: '1px solid rgba(110, 231, 183, 0.4)',
                padding: '3px 9px',
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#34D399', boxShadow: '0 0 8px #34D399' }} /> Live System
              </span>
            </div>
            <h1 style={{ margin: '0 0 8px', fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', color: '#fff' }}>
              Advansity Intelligence Dashboard
            </h1>
            <p style={{ margin: 0, fontSize: 14, color: '#C7D2FE', lineHeight: 1.5 }}>
              Central monitoring center for <strong>PAL Learning</strong>, <strong>Institutional Management</strong>, and <strong>The Advansity</strong> academic taxonomy.
            </p>
          </div>

          <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ textAlign: 'right', marginRight: 4 }}>
              <span style={{ fontSize: 11, color: '#A5B4FC', display: 'block', textTransform: 'uppercase', fontWeight: 600 }}>Sync Status</span>
              <span style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>
                {lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            </div>
            <button
              onClick={() => fetchStats(true)}
              disabled={refreshing}
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: '#fff',
                padding: '10px 16px',
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 600,
                cursor: refreshing ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.2s',
                backdropFilter: 'blur(4px)',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'}
            >
              <RefreshCw size={15} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>
        </div>

        {/* ─── Top Key Metric Cards ─── */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.g900, display: 'flex', alignItems: 'center', gap: 8 }}>
              <TrendingUp size={18} color={C.primary} /> Key Performance Indicators
            </h2>
            <span style={{ fontSize: 12, color: C.g500 }}>System-wide consolidated counts</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 16 }}>
            <MetricCard
              title="Registered Students"
              value={management?.students?.total}
              subtitle={`${management?.students?.active} Active accounts`}
              icon={Users}
              color="#3B82F6"
              bg="#EFF6FF"
              border="#DBEAFE"
              linkText="Manage Students"
              to="/students"
            />
            <MetricCard
              title="Affiliated Schools"
              value={management?.schools?.total}
              subtitle={`${management?.schools?.active} Verified active`}
              icon={School}
              color="#10B981"
              bg="#ECFDF5"
              border="#D1FAE5"
              linkText="Manage Schools"
              to="/schools"
            />
            <MetricCard
              title="Verified Teachers"
              value={management?.teachers?.total}
              subtitle={`${management?.teachers?.active} Faculty members`}
              icon={UserCheck}
              color="#8B5CF6"
              bg="#F5F3FF"
              border="#EDE9FE"
              linkText="Manage Teachers"
              to="/teachers"
            />
            <MetricCard
              title="Question Bank"
              value={advansity?.questions?.total}
              subtitle={`${advansity?.subjects} Subjects represented`}
              icon={FileQuestion}
              color="#F59E0B"
              bg="#FFFBEB"
              border="#FEF3C7"
              linkText="Browse Questions"
              to="/config/questions"
            />
            <MetricCard
              title="PAL Quizzes"
              value={pal?.totalQuizzes}
              subtitle={`${pal?.practice} Practice, ${pal?.tests} Tests, ${pal?.learning} Learn`}
              icon={BookOpen}
              color="#6366F1"
              bg="#EEF2FF"
              border="#E0E7FF"
              linkText="PAL Hub"
              to="/pal/practice"
            />
            <MetricCard
              title="Quiz Submissions"
              value={pal?.submissions}
              subtitle="Student completed attempts"
              icon={Award}
              color="#EC4899"
              bg="#FDF2F8"
              border="#FCE7F3"
              linkText="View Results"
              to="/results/tests"
            />
          </div>
        </div>

        {/* ─── SECTION 1: PAL (Personalized Adaptive Learning) Suite ─── */}
        <div style={{ background: '#fff', borderRadius: 18, border: `1px solid ${C.g200}`, padding: '24px 26px', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#EEF2FF', color: '#4F46E5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BookOpen size={18} />
                </div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.g900 }}>
                  PAL Suite (Personalized Adaptive Learning)
                </h2>
              </div>
              <p style={{ margin: '4px 0 0 40px', fontSize: 13, color: C.g500 }}>
                Counts and status of adaptive modules across Practice, Tests, Guided Learning, and Homework
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.primary, background: C.primaryGhost, padding: '6px 12px', borderRadius: 20 }}>
                Total: {pal?.totalQuizzes} Modules Active
              </span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 18 }}>
            {[
              { type: 'Practice', count: pal?.practice, ...PAL_TYPE_CONFIG['Practice'] },
              { type: 'Tests', count: pal?.tests, ...PAL_TYPE_CONFIG['Tests'] },
              { type: 'Learning', count: pal?.learning, ...PAL_TYPE_CONFIG['Learning'] },
              { type: 'Homework', count: pal?.homework, ...PAL_TYPE_CONFIG['Homework'] },
            ].map((item) => (
              <div
                key={item.type}
                style={{
                  background: item.bg,
                  borderRadius: 14,
                  border: `1px solid ${item.border}`,
                  padding: '20px 20px 18px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  position: 'relative',
                  transition: 'all 0.2s',
                }}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: item.color, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {item.type}
                    </span>
                    <span style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: item.color,
                      background: '#fff',
                      padding: '2px 12px',
                      borderRadius: 12,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    }}>
                      {item.count || 0}
                    </span>
                  </div>
                  <p style={{ margin: '0 0 16px', fontSize: 12, color: C.g600, lineHeight: 1.45, minHeight: 36 }}>
                    {item.desc}
                  </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, borderTop: `1px solid ${item.border}` }}>
                  <Link
                    to={item.path}
                    style={{ fontSize: 12, fontWeight: 600, color: item.color, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    View All {item.type} <ArrowRight size={13} />
                  </Link>
                  <Link
                    to={item.addPath}
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#fff',
                      background: item.color,
                      padding: '4px 9px',
                      borderRadius: 6,
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3
                    }}
                  >
                    <Plus size={12} /> Add
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ─── SECTION 2: Institutional Management Suite ─── */}
        <div style={{ background: '#fff', borderRadius: 18, border: `1px solid ${C.g200}`, padding: '24px 26px', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#ECFDF5', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Briefcase size={18} />
                </div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.g900 }}>
                  Management Submenu Items
                </h2>
              </div>
              <p style={{ margin: '4px 0 0 40px', fontSize: 13, color: C.g500 }}>
                Breakdown of Schools, Teachers, and Students registered in the Advansity ecosystem
              </p>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(310px, 1fr))', gap: 20 }}>
            {/* Schools Card */}
            <div style={{
              background: '#F9FAFB',
              borderRadius: 14,
              border: `1px solid ${C.g200}`,
              padding: 22,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Submenu Item</span>
                    <h3 style={{ margin: '2px 0 0', fontSize: 18, fontWeight: 700, color: C.g900 }}>Schools</h3>
                  </div>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: '#E0F2FE', color: '#0284C7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <School size={20} />
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '14px 0 10px' }}>
                  <span style={{ fontSize: 36, fontWeight: 800, color: C.g900, letterSpacing: '-0.02em' }}>
                    {management?.schools?.total?.toLocaleString()}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.success, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Check size={14} /> {management?.schools?.active} Active
                  </span>
                </div>

                <div style={{ background: '#E5E7EB', borderRadius: 6, height: 6, overflow: 'hidden', marginBottom: 12 }}>
                  <div style={{
                    width: `${management?.schools?.total > 0 ? (management?.schools?.active / management?.schools?.total) * 100 : 0}%`,
                    height: '100%',
                    background: '#10B981',
                    borderRadius: 6,
                  }} />
                </div>
                <p style={{ margin: 0, fontSize: 12, color: C.g500 }}>
                  Active rate: {management?.schools?.total > 0 ? ((management?.schools?.active / management?.schools?.total) * 100).toFixed(0) : 0}% verified institutions
                </p>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.g200}` }}>
                <button
                  onClick={() => navigate('/schools')}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    background: '#fff',
                    border: `1px solid ${C.g300}`,
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    color: C.g700,
                    cursor: 'pointer',
                  }}
                >
                  Manage Schools
                </button>
                <button
                  onClick={() => navigate('/school/add')}
                  style={{
                    padding: '8px 14px',
                    background: C.primary,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <Plus size={14} /> Add School
                </button>
              </div>
            </div>

            {/* Teachers Card */}
            <div style={{
              background: '#F9FAFB',
              borderRadius: 14,
              border: `1px solid ${C.g200}`,
              padding: 22,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Submenu Item</span>
                    <h3 style={{ margin: '2px 0 0', fontSize: 18, fontWeight: 700, color: C.g900 }}>Teachers</h3>
                  </div>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: '#EDE9FE', color: '#7C3AED', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <UserCheck size={20} />
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '14px 0 10px' }}>
                  <span style={{ fontSize: 36, fontWeight: 800, color: C.g900, letterSpacing: '-0.02em' }}>
                    {management?.teachers?.total?.toLocaleString()}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.success, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Check size={14} /> {management?.teachers?.active} Active
                  </span>
                </div>

                <div style={{ background: '#E5E7EB', borderRadius: 6, height: 6, overflow: 'hidden', marginBottom: 12 }}>
                  <div style={{
                    width: `${management?.teachers?.total > 0 ? (management?.teachers?.active / management?.teachers?.total) * 100 : 0}%`,
                    height: '100%',
                    background: '#8B5CF6',
                    borderRadius: 6,
                  }} />
                </div>
                <p style={{ margin: 0, fontSize: 12, color: C.g500 }}>
                  Active faculty members assigned to schools and subject streams
                </p>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.g200}` }}>
                <button
                  onClick={() => navigate('/teachers')}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    background: '#fff',
                    border: `1px solid ${C.g300}`,
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    color: C.g700,
                    cursor: 'pointer',
                  }}
                >
                  Manage Teachers
                </button>
                <button
                  onClick={() => navigate('/teacher/bulk-upload')}
                  style={{
                    padding: '8px 12px',
                    background: '#EDE9FE',
                    color: '#6D28D9',
                    border: '1px solid #DDD6FE',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Bulk Import
                </button>
              </div>
            </div>

            {/* Students Card */}
            <div style={{
              background: '#F9FAFB',
              borderRadius: 14,
              border: `1px solid ${C.g200}`,
              padding: 22,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Submenu Item</span>
                    <h3 style={{ margin: '2px 0 0', fontSize: 18, fontWeight: 700, color: C.g900 }}>Students</h3>
                  </div>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: '#DCFCE7', color: '#16A34A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Users size={20} />
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '14px 0 10px' }}>
                  <span style={{ fontSize: 36, fontWeight: 800, color: C.g900, letterSpacing: '-0.02em' }}>
                    {management?.students?.total?.toLocaleString()}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.success, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Check size={14} /> {management?.students?.active} Active
                  </span>
                </div>

                <div style={{ background: '#E5E7EB', borderRadius: 6, height: 6, overflow: 'hidden', marginBottom: 12 }}>
                  <div style={{
                    width: `${management?.students?.total > 0 ? (management?.students?.active / management?.students?.total) * 100 : 0}%`,
                    height: '100%',
                    background: '#10B981',
                    borderRadius: 6,
                  }} />
                </div>
                <p style={{ margin: 0, fontSize: 12, color: C.g500 }}>
                  Active learners utilizing adaptive learning & test platforms
                </p>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.g200}` }}>
                <button
                  onClick={() => navigate('/students')}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    background: '#fff',
                    border: `1px solid ${C.g300}`,
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    color: C.g700,
                    cursor: 'pointer',
                  }}
                >
                  Manage Students
                </button>
                <button
                  onClick={() => navigate('/student/bulk-upload')}
                  style={{
                    padding: '8px 12px',
                    background: '#DCFCE7',
                    color: '#15803D',
                    border: '1px solid #BBF7D0',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Bulk Import
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ─── SECTION 3: The Advansity Curriculum Hierarchy ─── */}
        <div style={{ background: '#fff', borderRadius: 18, border: `1px solid ${C.g200}`, padding: '24px 26px', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#FFF7ED', color: '#EA580C', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <GraduationCap size={18} />
                </div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.g900 }}>
                  The Advansity (Academic & Curriculum Hierarchy)
                </h2>
              </div>
              <p style={{ margin: '4px 0 0 40px', fontSize: 13, color: C.g500 }}>
                Live count of all 7 submenu items in the curriculum taxonomy from Boards to Questions
              </p>
            </div>
            <Link
              to="/config/questions"
              style={{ fontSize: 12, fontWeight: 600, color: C.primary, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              Open Configuration <ExternalLink size={14} />
            </Link>
          </div>

          {/* Visual Step Indicator / Flow */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#F8FAFC',
            borderRadius: 12,
            padding: '12px 18px',
            marginBottom: 20,
            border: `1px solid ${C.g200}`,
            overflowX: 'auto',
            gap: 12,
          }}>
            {[
              { label: 'Boards', count: advansity?.boards, path: '/config/board' },
              { label: 'Exams', count: advansity?.exams, path: '/config/exam' },
              { label: 'Classes', count: advansity?.classes, path: '/config/class' },
              { label: 'Subjects', count: advansity?.subjects, path: '/config/subject' },
              { label: 'Topics', count: advansity?.topics, path: '/config/topic' },
              { label: 'Sub Topics', count: advansity?.subTopics, path: '/config/sub-topic' },
              { label: 'Questions', count: advansity?.questions?.total, path: '/config/questions' },
            ].map((step, idx, arr) => (
              <React.Fragment key={step.label}>
                <div
                  onClick={() => navigate(step.path)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    padding: '4px 8px',
                    borderRadius: 6,
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#EEF2FF'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: C.primary,
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {idx + 1}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.g800 }}>{step.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.primary, background: '#E0E7FF', padding: '1px 6px', borderRadius: 10 }}>
                    {step.count}
                  </span>
                </div>
                {idx < arr.length - 1 && (
                  <ArrowRight size={14} color="#94A3B8" style={{ flexShrink: 0 }} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* 7 Curriculum Submenu Cards Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
            {[
              { label: 'Board', count: advansity?.boards, desc: 'Educational Boards', path: '/config/board', icon: Layers, color: '#4F46E5', bg: '#EEF2FF' },
              { label: 'Exam', count: advansity?.exams, desc: 'Competitive Exams', path: '/config/exam', icon: Award, color: '#06B6D4', bg: '#ECFEFF' },
              { label: 'Class', count: advansity?.classes, desc: 'Academic Classes', path: '/config/class', icon: School, color: '#8B5CF6', bg: '#F5F3FF' },
              { label: 'Subjects', count: advansity?.subjects, desc: 'Core Curriculum', path: '/config/subject', icon: BookOpen, color: '#10B981', bg: '#ECFDF5' },
              { label: 'Topic', count: advansity?.topics, desc: 'Syllabus Modules', path: '/config/topic', icon: Settings, color: '#F59E0B', bg: '#FFFBEB' },
              { label: 'Sub Topic', count: advansity?.subTopics, desc: 'Granular Concepts', path: '/config/sub-topic', icon: HelpCircle, color: '#EC4899', bg: '#FDF2F8' },
              { label: 'Questions', count: advansity?.questions?.total, desc: 'Assigned Bank', path: '/config/questions', icon: FileQuestion, color: '#EF4444', bg: '#FEF2F2' },
            ].map((sub) => {
              const SubIcon = sub.icon;
              return (
                <div
                  key={sub.label}
                  onClick={() => navigate(sub.path)}
                  style={{
                    background: '#fff',
                    borderRadius: 12,
                    border: `1px solid ${C.g200}`,
                    padding: '16px 16px 14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = sub.color;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.06)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = C.g200;
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ width: 34, height: 34, borderRadius: 8, background: sub.bg, color: sub.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <SubIcon size={17} />
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: sub.color, background: sub.bg, padding: '2px 7px', borderRadius: 10 }}>
                      Submenu
                    </span>
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: C.g900 }}>
                    {sub.count?.toLocaleString() || 0}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.g800, marginTop: 2 }}>
                    {sub.label}
                  </div>
                  <div style={{ fontSize: 11, color: C.g500, marginTop: 2 }}>
                    {sub.desc}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── SECTION 4: Charts & Visual Analytics ─── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 20 }}>
          {/* Question Bank by Subject Bar Chart */}
          <div style={{ background: '#fff', borderRadius: 18, border: `1px solid ${C.g200}`, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.g900, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <BarChart2 size={18} color={C.primary} /> Questions by Top Subjects
                </h3>
                <p style={{ margin: '3px 0 0', fontSize: 12, color: C.g500 }}>
                  Distribution of question bank across subject specializations
                </p>
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.primary }}>
                Total: {advansity?.questions?.total?.toLocaleString()}
              </span>
            </div>

            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={subjectChartData} margin={{ top: 10, right: 10, left: -10, bottom: 25 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: C.g600 }}
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    axisLine={{ stroke: '#E2E8F0' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: C.g500 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: 10, border: `1px solid ${C.g200}`, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                    formatter={(val, name, item) => [`${val} Questions`, item.payload.fullName]}
                  />
                  <Bar dataKey="count" fill="#4F46E5" radius={[6, 6, 0, 0]} maxBarSize={44} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Difficulty Level Breakdown Donut Chart */}
          <div style={{ background: '#fff', borderRadius: 18, border: `1px solid ${C.g200}`, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.g900, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Award size={18} color="#F59E0B" /> Question Difficulty Levels
                </h3>
                <p style={{ margin: '3px 0 0', fontSize: 12, color: C.g500 }}>
                  Difficulty grading allocation across the entire repository
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24, flexWrap: 'wrap', height: 260 }}>
              <div style={{ width: 180, height: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={difficultyChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      stroke="none"
                    >
                      {difficultyChartData.map((entry, idx) => (
                        <Cell key={`cell-${idx}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ borderRadius: 10, border: `1px solid ${C.g200}` }}
                      formatter={(val, name) => [`${val.toLocaleString()} questions`, name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 160 }}>
                {difficultyChartData.map((d) => {
                  const pct = advansity?.questions?.total > 0
                    ? ((d.value / advansity.questions.total) * 100).toFixed(1)
                    : 0;
                  return (
                    <div key={d.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
                        <span style={{ fontSize: 13, color: C.g700, fontWeight: 500 }}>{d.name}</span>
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: C.g900 }}>
                        {d.value.toLocaleString()} <span style={{ fontSize: 11, fontWeight: 400, color: C.g400 }}>({pct}%)</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ─── SECTION 5: Recent PAL Quizzes & Submissions ─── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 20 }}>
          {/* Recent Quizzes */}
          <div style={{ background: '#fff', borderRadius: 18, border: `1px solid ${C.g200}`, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.g900, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Clock size={16} color={C.primary} /> Recent PAL Modules
              </h3>
              <Link to="/pal/practice" style={{ fontSize: 12, fontWeight: 600, color: C.primary, textDecoration: 'none' }}>
                View All →
              </Link>
            </div>

            {(!recentQuizzes || recentQuizzes.length === 0) ? (
              <p style={{ fontSize: 13, color: C.g500, margin: '20px 0', textAlign: 'center' }}>No quizzes created yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {recentQuizzes.map((q) => {
                  const typeCfg = PAL_TYPE_CONFIG[q.quiz_type] || PAL_TYPE_CONFIG['Practice'];
                  return (
                    <div
                      key={q.id}
                      onClick={() => navigate(typeCfg.path)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 14px',
                        background: '#F9FAFB',
                        borderRadius: 10,
                        border: `1px solid ${C.g100}`,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#F3F4F6';
                        e.currentTarget.style.borderColor = C.g200;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#F9FAFB';
                        e.currentTarget.style.borderColor = C.g100;
                      }}
                    >
                      <div style={{ maxWidth: '65%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: typeCfg.color, background: typeCfg.bg, padding: '1px 6px', borderRadius: 4 }}>
                            {q.quiz_type}
                          </span>
                          <span style={{ fontSize: 11, color: C.g400 }}>
                            {q.created_at ? new Date(q.created_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.g900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {q.name}
                        </div>
                      </div>

                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: C.g700, display: 'block' }}>
                          {q.question_count} Qs
                        </span>
                        <span style={{ fontSize: 11, color: q.status === 'Active' || q.status === 'active' ? C.success : C.g500, fontWeight: 500 }}>
                          {q.status || 'Active'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Recent Quiz Submissions */}
          <div style={{ background: '#fff', borderRadius: 18, border: `1px solid ${C.g200}`, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.g900, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Award size={16} color="#10B981" /> Latest Student Submissions
              </h3>
              <Link to="/results/tests" style={{ fontSize: 12, fontWeight: 600, color: C.primary, textDecoration: 'none' }}>
                View All Results →
              </Link>
            </div>

            {(!recentSubmissions || recentSubmissions.length === 0) ? (
              <p style={{ fontSize: 13, color: C.g500, margin: '20px 0', textAlign: 'center' }}>No submissions recorded yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {recentSubmissions.map((sub) => (
                  <div
                    key={sub.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 14px',
                      background: '#F9FAFB',
                      borderRadius: 10,
                      border: `1px solid ${C.g100}`,
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: C.g900 }}>
                          {sub.student_name || 'Student'}
                        </span>
                        {sub.quiz_type && (
                          <span style={{ fontSize: 10, fontWeight: 600, color: C.primary, background: C.primaryGhost, padding: '1px 5px', borderRadius: 4 }}>
                            {sub.quiz_type}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: C.g600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                        {sub.quiz_name || 'Quiz'}
                      </div>
                    </div>

                    <div style={{ textAlign: 'right' }}>
                      <span style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: sub.score > 0 ? '#059669' : C.g700,
                        background: sub.score > 0 ? '#DCFCE7' : '#F3F4F6',
                        padding: '3px 8px',
                        borderRadius: 6,
                        display: 'inline-block',
                      }}>
                        {sub.score ?? 0} / {sub.total_questions ?? '-'}
                      </span>
                      <span style={{ fontSize: 11, color: C.g400, display: 'block', marginTop: 2 }}>
                        {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>
    </AppLayout>
  );
};

export default AdvansityDashboard;
